import { useEffect, useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import WhatsAppWidget from '../components/WhatsAppWidget';
import CountrySelect from '../components/CountrySelect';

const API_BASE = process.env.REACT_APP_API_BASE_URL || `${process.env.FRONTEND_URL}/api`;

export default function CourseDirectoryPage() {
  const [courses, setCourses] = useState([]);
  const [meta, setMeta] = useState({ total: 0 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: '', country: '', degreeLevel: '', fieldOfStudy: '', tuitionMax: '', hasDiscount: false });
  const [selectedCourse, setSelectedCourse] = useState(null);

  const fetchCourses = () => {
    setLoading(true);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
    fetch(`${API_BASE}/courses?${params.toString()}`)
      .then((r) => r.json())
      .then((json) => {
        if (json.success) {
          setCourses(json.data);
          setMeta(json.meta);
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(fetchCourses, [filters]);

  const clearFilters = () => setFilters({ search: '', country: '', degreeLevel: '', fieldOfStudy: '', tuitionMax: '', hasDiscount: false });

  return (
    <div className="bg-[#f7f9fb] font-['Plus_Jakarta_Sans'] min-h-screen">
      <Navbar />
      <main className="pt-20">
        <section className="py-20 px-6 bg-[#00255a] text-white">
          <div className="max-w-[1280px] mx-auto text-center md:text-left">
            <h1 className="text-5xl font-bold mb-4">Find Courses for Study Abroad</h1>
            <p className="text-lg max-w-2xl opacity-90">
              Search international courses across our partner destinations and compare programmes that fit your study abroad goals, budget and career plans.
            </p>
          </div>
        </section>

        {/* Sticky Filter Bar */}
        <div className="sticky top-20 z-40 bg-[#f7f9fb] border-b border-[#e2e8f0] py-6 px-6 shadow-sm">
          <div className="max-w-[1280px] mx-auto space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-center">
              <div className="lg:col-span-4 relative">
                <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-[#64748b]">search</span>
                <input
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  placeholder="Search international courses or fields of study..."
                  className="w-full pl-12 pr-4 py-3 bg-white border border-[#c4c6d1] rounded-xl focus:ring-2 focus:ring-[#00255a]/10 focus:border-[#00255a] outline-none transition-all"
                />
              </div>
              <div className="lg:col-span-2">
                <CountrySelect value={filters.country} onChange={(country) => setFilters({ ...filters, country })} className="w-full px-4 py-3 bg-white border border-[#c4c6d1] rounded-xl outline-none" />
              
              </div>
              <div className="lg:col-span-2">
                <select
                  value={filters.degreeLevel}
                  onChange={(e) => setFilters({ ...filters, degreeLevel: e.target.value })}
                  className="w-full px-4 py-3 bg-white border border-[#c4c6d1] rounded-xl outline-none"
                >
                  <option value="">Degree Level</option>
                  <option value="Bachelor's">Bachelor's</option>
                  <option value="Master's">Master's</option>
                </select>
              </div>
              <div className="lg:col-span-2">
                <select
                  value={filters.fieldOfStudy}
                  onChange={(e) => setFilters({ ...filters, fieldOfStudy: e.target.value })}
                  className="w-full px-4 py-3 bg-white border border-[#c4c6d1] rounded-xl outline-none"
                >
                  <option value="">Field of Study</option>
                  <option value="Engineering & Technology">Engineering & Technology</option>
                  <option value="Business & Economics">Business & Economics</option>
                  <option value="Health & Medical">Health & Medical</option>
                </select>
              </div>
              <div className="lg:col-span-2 px-2">
                <label className="block text-xs text-[#64748b] mb-1">Max Tuition ($)</label>
                <input
                  type="number"
                  value={filters.tuitionMax}
                  onChange={(e) => setFilters({ ...filters, tuitionMax: e.target.value })}
                  placeholder="No limit"
                  className="w-full px-3 py-2 border border-[#c4c6d1] rounded-lg outline-none text-sm"
                />
              </div>
              <label className="lg:col-span-2 flex items-center gap-2 px-2 cursor-pointer text-sm font-semibold text-[#00255a]">
                <input type="checkbox" checked={filters.hasDiscount} onChange={(e) => setFilters({ ...filters, hasDiscount: e.target.checked })} className="w-4 h-4" />
                Discount available
              </label>
            </div>
            <div className="flex justify-between items-center">
              <p className="text-sm text-[#44474f] flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#ff8483] animate-pulse" />
                Showing {courses.length} of {meta.total} courses
              </p>
              <button onClick={clearFilters} className="flex items-center gap-2 text-[#00255a] font-bold text-sm">
                <span className="material-symbols-outlined">tune</span> Clear All Filters
              </button>
            </div>
          </div>
        </div>

        {/* Course Grid */}
        <section className="max-w-[1280px] mx-auto px-6 py-12">
          {loading ? (
            <p className="text-center text-[#64748b] py-20">Loading courses...</p>
          ) : courses.length === 0 ? (
            <p className="text-center text-[#64748b] py-20">No courses match your filters.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {courses.map((course) => (
                <CourseCard key={course.id} course={course} onView={() => setSelectedCourse(course)} />
              ))}
            </div>
          )}
        </section>
      </main>

      {selectedCourse && <CourseModal course={selectedCourse} onClose={() => setSelectedCourse(null)} />}

      <Footer />
      <WhatsAppWidget />
    </div>
  );
}

function CourseCard({ course, onView }) {
  const discountedUniversities = (course.institutions || []).map((link) => link.institution).filter((institution) => institution?.discountFee != 0 || null);
  return (
    <div className="bg-white border border-[#e2e8f0] rounded-2xl p-6 shadow-[0px_10px_30px_rgba(30,60,114,0.05)] hover:shadow-[0px_20px_40px_rgba(30,60,114,0.12)] hover:-translate-y-1 transition-all group">
      <div className="flex gap-2 mb-4">
        <span className="bg-[#00255a]/5 text-[#00255a] px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider">{course.country}</span>
        <span className="bg-[#365da3]/5 text-[#365da3] px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider">{course.degreeLevel}</span>
        {discountedUniversities.length > 0 && <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider">Discount available</span>}
      </div>
      <h3 className="text-xl font-semibold mb-2 group-hover:text-[#00255a] transition-colors">{course.courseName}</h3>
      <p className="inline-flex items-center px-3 py-1 bg-[#ffdad8] text-[#862026] rounded-full text-[11px] font-bold mb-4">
        {course.fieldOfStudy}
      </p>
      <div className="space-y-2 mb-6">
        <div className="flex items-center justify-between">
          <span className="text-[#64748b] text-sm">Tuition fee</span>
          <span className="font-bold text-[#ff7b7b]">
            {course.tuitionFeeAmount ? `From ${course.tuitionFeeCurrency} ${course.tuitionFeeAmount}${course.tuitionFeePeriod ? '/' + course.tuitionFeePeriod.replace('per ', '') : ''}` : 'Contact for pricing'}
          </span>
        </div>
        {/* University field intentionally low-visibility — Phase 3 linking, not yet populated for most courses */}
        <p className="text-[13px] text-[#747781] italic">
          {course.institutions?.length ? `${course.institutions.length} affiliated universities` : `University: ${course.institution?.name || course.institutionNameRaw || 'Not specified'}`}
        </p>
      </div>
      <button
        onClick={onView}
        className="w-full py-3 border-2 border-[#00255a] text-[#00255a] font-bold rounded-xl hover:bg-[#00255a] hover:text-white transition-all"
      >
        View Requirements
      </button>
    </div>
  );
}

function CourseModal({ course, onClose }) {
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-[#0f172a]/60 backdrop-blur-sm" onClick={onClose} />
      <div className="bg-white rounded-3xl w-full max-w-4xl relative z-10 overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="h-48 relative bg-[#00255a] flex items-end p-8">
          <button onClick={onClose} className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition-all">
            <span className="material-symbols-outlined">close</span>
          </button>
          <div className="relative z-10">
            <div className="flex gap-2 mb-3">
              <span className="bg-white/20 text-white px-3 py-1 rounded-full text-[12px] font-bold uppercase tracking-wider">{course.degreeLevel} Program</span>
            </div>
            <h2 className="text-3xl font-bold text-white">{course.courseName}</h2>
          </div>
        </div>
        <div className="p-8 md:p-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12 border-b border-[#e2e8f0] pb-12">
            <ModalStat label="Degree" value={course.degreeLevel} />
            <ModalStat label="Field" value={course.fieldOfStudy} />
            <ModalStat label="Language" value={course.languageOfInstruction || '—'} />
            <ModalStat label="Duration" value={course.durationYears ? `${course.durationYears} Year${course.durationYears > 1 ? 's' : ''}` : '—'} />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h4 className="text-xl font-semibold mb-6">Requirements Checklist</h4>
              <ul className="space-y-4">
                {(course.requirements || []).map((req, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="material-symbols-outlined text-[#ff7b7b]">check_circle</span>
                    <span>{req}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-[#f2f4f6] p-6 rounded-2xl border border-[#e2e8f0]">
              <h4 className="text-xl font-semibold mb-4">Tuition Breakdown</h4>
              <div className="space-y-4 mb-6">
                <div className="flex justify-between border-b border-[#c4c6d1]/30 pb-2">
                  <span className="text-[#44474f]">Tuition Fee</span>
                  <span className="font-bold text-[#00255a]">
                    {course.tuitionFeeAmount ? `${course.tuitionFeeCurrency} ${course.tuitionFeeAmount} ${course.tuitionFeePeriod || ''}` : '—'}
                  </span>
                </div>
                {course.agencyApplicationFee && (
                  <div className="flex justify-between border-b border-[#c4c6d1]/30 pb-2">
                    <span className="text-[#44474f]">Agency Application Fee</span>
                    <span className="font-bold text-[#00255a]">${course.agencyApplicationFee}</span>
                  </div>
                )}
              </div>
              <p className="text-[12px] text-[#64748b] italic">* Prices are estimates and subject to change by the institution.</p>
            </div>
          </div>
          <div className="mt-10 border-t border-[#e2e8f0] pt-8">
            <h4 className="text-xl font-semibold text-[#00255a]">Affiliated universities</h4>
            <p className="text-sm text-[#64748b] mt-1">Partner universities tagged for this course and their current discounted tuition.</p>
            <div className="mt-4 grid md:grid-cols-2 gap-4">
              {(course.institutions?.map((link) => link.institution) || (course.institution ? [course.institution] : [])).map((university) => (
                <div key={university.id} className="rounded-xl border border-[#e2e8f0] p-4 bg-white">
                  <p className="font-bold text-[#00255a]"><a href={`/universities/${university.id}`} target="_blank" rel="noreferrer">{university.name}</a></p><p className="text-xs text-[#64748b]">{university.country}</p>
                  <p className="mt-3 text-sm">{university.normalFee && <span className="line-through text-[#747781] mr-2">{university.feeCurrency} {university.normalFee}</span>}<b className="text-[#00255a]">{university.discountFee ? `${university.feeCurrency} ${university.discountFee}` : 'Contact for price'} discount</b></p>
                  {university.websiteUrl && <a href={university.websiteUrl} target="_blank" rel="noreferrer" className="inline-block mt-3 text-sm text-[#365da3] font-bold underline">Official website ↗</a>}
                </div>
              ))}
              {!course.institutions?.length && !course.institution && <p className="text-sm text-[#64748b]">No university has been tagged for this course yet.</p>}
            </div>
          </div>
          <div className="mt-12 flex flex-col md:flex-row items-center justify-between gap-6 pt-8 border-t border-[#e2e8f0]">
            <p className="text-[#44474f] text-center md:text-left">Interested in this course? Start your journey today.</p>
            <a
              href={`/apply/study?fieldOfStudy=${encodeURIComponent(course.fieldOfStudy)}&country=${encodeURIComponent(course.country)}`}
              className="text-white px-10 py-4 rounded-full font-bold text-lg shadow-xl hover:scale-105 transition-transform flex items-center gap-3"
              style={{ background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 60%, #ff7b7b 100%)' }}
            >
              Apply Now <span className="material-symbols-outlined">trending_flat</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function ModalStat({ label, value }) {
  return (
    <div className="space-y-1">
      <p className="text-sm text-[#64748b]">{label}</p>
      <p className="font-bold text-[#00255a]">{value}</p>
    </div>
  );
}
